// Shared thermal-receipt rendering for the POS. Used by both the checkout screen
// (Pos.js, printed right after a sale) and the sales list (PosSales.js, reprinted
// on demand). Keeping it in one place stops the two receipts from drifting apart.

// Fallback business/branch details for the receipt header, used only when the
// logged-in console user has no assigned laundry. Real values come from the user's
// laundry (laundryName / laundryAddress / laundryPhone), captured at login.
export const BUSINESS_INFO = {
    name: "Indry Juffair",
    address: "Shop 18, Juffair",
    tel: "36102111",
};

export const escapeHtml = (value) =>
    String(value == null ? "" : value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

export const money3 = (value) => (Number(value) || 0).toFixed(3);

export const pad2 = (n) => String(n).padStart(2, "0");

// Human-friendly ready/pickup date for the receipt, e.g. "25/06/26 14:30".
export const formatReadyDate = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

// Builds the thermal-receipt inner HTML from an order data object.
// `copyLabel` distinguishes the customer's collection copy from the shop's retained copy.
// Used for both the on-screen preview and the printed receipt.
export const buildReceiptBody = (data, copyLabel = "Customer Copy") => {
    const biz = data.business || BUSINESS_INFO;
    // "Pay on Collection" is settled at pickup, so it's unpaid at drop-off; everything else
    // is taken now. PAID reflects that so the receipt is a real payment record.
    const isPaid = (data.paymentMethod || "").toLowerCase() !== "pay on collection";
    const amountPaid = isPaid ? (Number(data.total) || 0) : 0;
    const expressLabel = data.express ? "EXPRESS" : "";
    const itemRows = data.items.map((it) => `
        <tr>
            <td style="padding:3px 3px 3px 0; vertical-align:top; word-break:break-word;">${escapeHtml(it.name)}${it.serviceName ? ` (${escapeHtml(it.serviceName)})` : ""}</td>
            <td style="padding:3px 3px; text-align:right; white-space:nowrap; vertical-align:top; width:1%;">${money3(it.price)}</td>
            <td style="padding:3px 3px; text-align:center; white-space:nowrap; vertical-align:top; width:1%;">${it.qty}</td>
            <td style="padding:3px 0; text-align:right; white-space:nowrap; vertical-align:top; width:1%;">${money3(it.total)}</td>
        </tr>`).join("");

    const totalRow = (label, value, bold) => `
        <tr>
            <td style="padding:1px 0;${bold ? "font-weight:bold;" : ""}">${label}</td>
            <td style="padding:1px 0; text-align:right;${bold ? "font-weight:bold;" : ""}">${money3(value)}</td>
        </tr>`;

    return `
    <div style="font-family:'Courier New',monospace; color:#000; font-weight:bold; font-size:12px; line-height:1.5; width:100%; max-width:300px; margin:0 auto;">
        <div style="text-align:center;">
            <div style="font-weight:bold; text-transform:uppercase; letter-spacing:1px;">${escapeHtml(copyLabel)}</div>
            <div style="font-size:20px; font-weight:bold; margin-top:6px;">${escapeHtml(data.orderNo)}</div>
            <div style="font-size:17px; font-weight:bold;">${data.pieces} Pieces</div>
            <div style="font-size:17px; font-weight:bold; margin-top:8px;">${escapeHtml(biz.name)}</div>
            ${biz.address ? `<div>${escapeHtml(biz.address).replace(/\n/g, "<br>")}</div>` : ""}
            ${biz.tel ? `<div>Tel: ${escapeHtml(biz.tel)}</div>` : ""}
            ${data.servedBy ? `<div>Served By: ${escapeHtml(data.servedBy)}</div>` : ""}
            <div style="font-size:16px; font-weight:bold; margin-top:10px; text-transform:uppercase;">${escapeHtml(data.customerName)}</div>
            ${data.customerRef ? `<div>${escapeHtml(data.customerRef)}</div>` : ""}
            ${expressLabel ? `<div style="font-size:15px; font-weight:bold; margin-top:8px; text-transform:uppercase;">${escapeHtml(expressLabel)}</div>` : ""}
        </div>
        <div style="border-top:1px dashed #000; margin:8px 0;"></div>
        <table style="width:100%; border-collapse:collapse; font-size:12px;">
            <thead>
                <tr style="border-bottom:1px solid #000;">
                    <th style="text-align:left; padding:0 3px 4px 0; font-weight:bold;">Item</th>
                    <th style="text-align:right; padding:0 3px 4px; font-weight:bold; width:1%; white-space:nowrap;">Price</th>
                    <th style="text-align:center; padding:0 3px 4px; font-weight:bold; width:1%; white-space:nowrap;">Qty</th>
                    <th style="text-align:right; padding:0 0 4px; font-weight:bold; width:1%; white-space:nowrap;">Total</th>
                </tr>
            </thead>
            <tbody>${itemRows}</tbody>
        </table>
        <div style="border-top:1px dashed #000; margin:8px 0;"></div>
        <table style="width:100%; border-collapse:collapse; font-size:12px;">
            ${totalRow("SUBTOTAL:", data.subtotal)}
            ${data.discount > 0 ? totalRow("DISCOUNT:", -data.discount) : ""}
            ${data.deliveryCharge > 0 ? totalRow("DELIVERY:", data.deliveryCharge) : ""}
            ${data.vat > 0 ? totalRow("VAT:", data.vat) : ""}
            ${totalRow("TOTAL:", data.total, true)}
            ${totalRow("PAID:", amountPaid)}
        </table>
        <div style="border-top:1px dashed #000; margin:8px 0;"></div>
        <div style="text-align:center; font-size:15px; font-weight:bold; margin-top:6px; padding:3px 0; border:1px solid #000;">${(isPaid ? "PAID" : "UNPAID") + (data.paymentMethod ? " — " + escapeHtml(String(data.paymentMethod).toUpperCase()) : "")}</div>
        <div style="text-align:center; margin-top:8px;">Dropped Off: ${escapeHtml(data.date)}</div>
        ${data.readyDate ? `<div style="text-align:center; margin-top:4px;">Ready By: ${escapeHtml(data.readyDate)}</div>` : `<div style="text-align:center; font-size:17px; margin-top:10px;">Due:</div>`}
        <div style="text-align:center; font-size:18px; font-weight:bold; margin-top:14px;">${escapeHtml(data.orderNo)}</div>
    </div>`;
};

// Prints the receipt from a hidden iframe rather than a pop-up window.
//
// The pop-up version left the shop unable to print twice: it opened a window named
// "PRINT" and closed it from `onafterprint`, but that handler was attached after the
// window had already been told to print, so a window whose print finished first was
// never closed — and the next print reused that same name, targeting a window stuck
// behind a modal print dialog. Since a same-origin pop-up shares the renderer's event
// loop with the app, that dialog froze the whole POS until the browser was restarted.
//
// An iframe cannot be left orphaned: it is removed on afterprint and, for browsers
// that skip that event, by the fallback timer.
export const printReceipt = (data, copyLabel = "Customer Copy") => {
    // One copy per print job so each comes out as its own ticket (the printer cuts between
    // separate jobs). The customer copy is always printed; the store copy is an optional
    // second job. copyLabel selects which one this frame renders.
    const body = buildReceiptBody(data, copyLabel);
    const frame = document.createElement("iframe");
    frame.setAttribute("aria-hidden", "true");
    frame.style.position = "fixed";
    frame.style.right = "0";
    frame.style.bottom = "0";
    frame.style.width = "0";
    frame.style.height = "0";
    frame.style.border = "0";
    frame.style.visibility = "hidden";
    document.body.appendChild(frame);

    let cleanedUp = false;
    const cleanUp = () => {
        if (cleanedUp) return;
        cleanedUp = true;
        // Detaching during the print job cancels it, so always leave the browser a beat.
        setTimeout(() => {
            if (frame.parentNode) frame.parentNode.removeChild(frame);
        }, 500);
    };

    const win = frame.contentWindow;
    if (!win) {
        cleanUp();
        alert("Could not open the receipt for printing.");
        return;
    }

    // Attached before anything can trigger a print, so the frame is always reclaimed.
    win.onafterprint = cleanUp;
    // Safari and some Edge builds never fire afterprint; this keeps frames from piling up
    // over a long shift.
    setTimeout(cleanUp, 60000);

    win.document.write(`<!DOCTYPE html><html><head><title>Receipt ${escapeHtml(data.orderNo)} — ${escapeHtml(copyLabel)}</title>
        <style>
            /* Paper is 70mm wide (7cm). Pinning the layout to that physical width stops the
               browser from laying it out wider (which makes the printer chop both edges).
               Generous side padding keeps content out of the printer's non-printable left/right
               dead zones, so the left column isn't clipped and the centered header stays centered. */
            @page { size: 70mm auto; margin: 0; }
            * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            /* Thermal heads render thin strokes faintly; bold weight + pure black darkens the whole ticket. */
            html, body { width: 70mm; margin: 0; color: #000; font-weight: bold; }
            body { padding: 3mm 5mm 6mm 7mm; }
            /* Let the receipt fill the printable width instead of the on-screen preview cap. */
            .receipt-copy > div { width: 100% !important; max-width: 100% !important; margin: 0 !important; }
        </style></head>
        <body>
            <div class="receipt-copy">${body}</div>
        </body></html>`);
    win.document.close();

    // Printed from here rather than from an inline onload in the written document, so the
    // print is only asked for once the frame is parsed and this side is ready to clean up.
    const print = () => {
        try {
            win.focus();
            win.print();
        } catch (err) {
            console.error("Receipt print failed:", err);
            cleanUp();
        }
    };
    if (win.document.readyState === "complete") {
        setTimeout(print, 0);
    } else {
        win.onload = print;
    }
};
